import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/main.scss';

const SentryFallback = () => (
  <p>Došlo je do greške. Molimo osvježite stranicu.</p>
);

// Defer the Sentry SDK (~80 KB gzipped, pulls its own chunk) until
// after the initial paint so it doesn't block first render. We still
// wire up an ErrorBoundary synchronously — it'll lazy-init Sentry on
// the way to reporting if it ever trips.
async function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // CI sets VITE_SENTRY_ENV to the instance name (vox, chello, ...) so
    // events are filterable per customer; falls back to the Vite mode.
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err: Error, info: React.ErrorInfo) {
    // Forward to Sentry if/when it's loaded.
    import('@sentry/react').then((Sentry) => {
      Sentry.captureException(err, { extra: { componentStack: info.componentStack } });
    }).catch(() => { /* ignore */ });
  }
  render() {
    if (this.state.hasError) return <SentryFallback />;
    return this.props.children;
  }
}

// Auto-reload on stale chunk errors after deploy
function handleChunkError() {
  const reloaded = sessionStorage.getItem('chunk-reload');
  if (!reloaded) {
    sessionStorage.setItem('chunk-reload', '1');
    window.location.reload();
  }
}
window.addEventListener('vite:preloadError', handleChunkError);
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.message?.includes('Failed to fetch dynamically imported module')) {
    e.preventDefault();
    handleChunkError();
  }
});
// Clear flag on successful load
sessionStorage.removeItem('chunk-reload');

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Kick off Sentry after the initial render is committed.
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as unknown as { requestIdleCallback: (cb: () => void) => void })
      .requestIdleCallback(() => { initSentry(); });
  } else {
    setTimeout(initSentry, 0);
  }
}
